import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Wallet, Loader2, IndianRupee, Eye, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

export const MyPayslipsPage = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const handleDownload = async (p) => {
    if (!p) return;
    setActionBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const tokenRaw = localStorage.getItem('access_token') || '';
      const r = await fetch(`${base}/api/payroll/${p.id}/payslip-pdf?ts=${Date.now()}`, {
        headers: { Authorization: `Bearer ${tokenRaw}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip-${p.month}-${p.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally { setActionBusy(false); }
  };

  const handleEmail = async (p) => {
    if (!p) return;
    setActionBusy(true);
    try {
      const r = await api.post(`/payroll/${p.id}/email`);
      toast.success(r.data?.message || 'Payslip emailed to your official email');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email send failed');
    } finally { setActionBusy(false); }
  };

  useEffect(() => { fetchPayslips(); }, [year]);

  const fetchPayslips = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/my/payslips?year=${year}`);
      if (res.data.success) setPayslips(res.data.data);
    } catch { setPayslips([]); }
    finally { setLoading(false); }
  };

  const totalEarnings = payslips.reduce((s, p) => s + (p.net_salary || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-payslips-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">My Payslips</h1><p className="text-muted-foreground">View your salary details</p></div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[90px]" data-testid="my-payslip-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(() => {
              const cur = new Date().getFullYear();
              // Show current year, last 10 years (covers all migrated legacy data),
              // and next year for upcoming payslips.
              const years = [];
              for (let y = cur + 1; y >= cur - 10; y--) years.push(y);
              return years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>);
            })()}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="my-payslip-count">{payslips.length}</p><p className="text-xs text-muted-foreground">Payslips in {year}</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-500" /></div><div><p className="text-xl font-bold" data-testid="my-payslip-total">{formatCurrency(totalEarnings)}</p><p className="text-xs text-muted-foreground">Total Earnings</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        payslips.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Wallet className="h-12 w-12 mb-4 opacity-50" /><p>No payslips found for {year}</p></div> :
        <Table data-testid="my-payslips-table"><TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Basic</TableHead><TableHead>Days</TableHead><TableHead>Overtime</TableHead><TableHead>Deductions</TableHead><TableHead>Net Salary</TableHead><TableHead>Status</TableHead><TableHead className="w-[60px]"></TableHead></TableRow></TableHeader>
        <TableBody>{payslips.map(p => (
          <TableRow key={p.id} data-testid={`my-payslip-row-${p.id}`}>
            <TableCell className="font-medium">{p.month} {p.year}</TableCell>
            <TableCell>{formatCurrency(p.basic_salary)}</TableCell>
            <TableCell><span className="font-medium">{p.days_present}</span><span className="text-muted-foreground">/{p.total_working_days}</span></TableCell>
            <TableCell>{p.overtime_hours > 0 ? <span className="text-amber-500">{p.overtime_hours}h (+{formatCurrency(p.overtime_pay)})</span> : '-'}</TableCell>
            <TableCell className="text-destructive">-{formatCurrency(p.total_deductions)}</TableCell>
            <TableCell className="font-semibold">{formatCurrency(p.net_salary)}</TableCell>
            <TableCell><Badge className={p.status === 'paid' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}>{p.status}</Badge></TableCell>
            <TableCell><Button variant="ghost" size="icon" onClick={() => { setSelected(p); setDialogOpen(true); }} data-testid={`view-payslip-${p.id}`}><Eye className="h-4 w-4" /></Button></TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>

      {/* Payslip Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="my-payslip-dialog">
          <DialogHeader><DialogTitle>Payslip - {selected?.month} {selected?.year}</DialogTitle></DialogHeader>
          {selected && <div className="space-y-3">
            <div className="space-y-2 text-sm">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Earnings</h4>
              <div className="flex justify-between"><span className="text-muted-foreground">Basic Salary</span><span>{formatCurrency(selected.basic_salary)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">HRA (40%)</span><span>{formatCurrency(selected.hra)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Allowances</span><span>{formatCurrency(selected.allowances)}</span></div>
              {selected.overtime_pay > 0 && <div className="flex justify-between text-amber-500"><span>Overtime ({selected.overtime_hours}h)</span><span>+{formatCurrency(selected.overtime_pay)}</span></div>}

              <h4 className="text-xs font-semibold text-muted-foreground uppercase pt-2 border-t border-border">Attendance</h4>
              <div className="flex justify-between"><span className="text-muted-foreground">Working Days</span><span>{selected.total_working_days}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Days Present</span><span>{selected.days_present}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Days Absent</span><span className={selected.days_absent > 0 ? 'text-destructive' : ''}>{selected.days_absent}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Daily Rate</span><span>{formatCurrency(selected.daily_rate)}</span></div>

              <h4 className="text-xs font-semibold text-muted-foreground uppercase pt-2 border-t border-border">Deductions</h4>
              <div className="flex justify-between text-destructive"><span>Statutory (PF)</span><span>-{formatCurrency(selected.statutory_deductions)}</span></div>
              {selected.absence_deduction > 0 && <div className="flex justify-between text-destructive"><span>Absence</span><span>-{formatCurrency(selected.absence_deduction)}</span></div>}

              <div className="flex justify-between text-base font-bold border-t border-border pt-2"><span>Net Salary</span><span className="text-primary">{formatCurrency(selected.net_salary)}</span></div>
            </div>
          </div>}
          {selected && (
            <DialogFooter className="border-t border-border pt-4">
              <Button variant="outline" onClick={() => handleEmail(selected)} disabled={actionBusy} data-testid="my-payslip-email-btn">
                <Mail className="h-4 w-4 mr-2" />Email me a copy
              </Button>
              <Button onClick={() => handleDownload(selected)} disabled={actionBusy} data-testid="my-payslip-download-btn">
                {actionBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
